import { Flex, Space, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../../Components/Button"
import { titleSortFilters } from "../../../../../Components/titleSortFilters"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { alertsColumnIconMap, dateFormat } from "../../../../../Utils/utils"
import { PROGRAM_META } from "../../constants"
import StatusTag from "../StatusTag/StatusTag"

dayjs.extend(utc)

const { Text } = Typography

const dateCell = (value) =>
  value ? (
    <Space direction="vertical" size={4}>
      <Text>{dayjs.utc(value).format(dateFormat)}</Text>
      <Text type="secondary">{dayjs.utc(value).format("h:mm A [(UTC)]")}</Text>
    </Space>
  ) : (
    <Text>-</Text>
  )

const ProgramsCell = ({ programs }) => {
  if (!programs?.length) return <Text>-</Text>
  return (
    <Flex gap={12} align="center" wrap="wrap">
      {programs.map((p) => {
        const meta = PROGRAM_META[p]
        if (!meta) return null
        return (
          <Flex key={p} gap={6} align="center">
            {meta.img && Images[meta.img] ? (
              <img src={Images[meta.img]} alt={meta.label} width={20} height={20} />
            ) : null}
            <Text>{meta.label}</Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

export const getColumns = ({ onStatusChange, onBusinessAccount }) => ({
  id: {
    key: "sequence_number",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "",
        sorter: true,
        icon: alertsColumnIconMap["id"],
        stateData: state,
        key: "sequence_number"
      }),
    dataIndex: "sequence_number",
    width: 60,
    maxWidth: 70,
    render: (value) => <Text>{value ?? "-"}</Text>
  },
  status: {
    key: "status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Status",
        sorter: true,
        icon: alertsColumnIconMap["status"],
        stateData: state,
        key: "status"
      }),
    dataIndex: "status",
    width: 170,
    render: (status, record) => {
      if (!status) return <Text>-</Text>
      return (
        <StatusTag
          status={status}
          withDropdown
          onChange={(newStatus) => onStatusChange({ id: record.id, newStatus })}
        />
      )
    }
  },
  programs: {
    key: "programs",
    title: (state) =>
      titleSortFilters({ title: "Programs", icon: "card", stateData: state, key: "programs" }),
    dataIndex: "programs",
    width: 180,
    render: (programs) => <ProgramsCell programs={programs} />
  },
  created_timestamp: {
    key: "created_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Request date",
        sorter: true,
        icon: alertsColumnIconMap["created_timestamp"],
        stateData: state,
        key: "created_timestamp"
      }),
    dataIndex: "created_timestamp",
    width: 170,
    render: dateCell
  },
  updated_timestamp: {
    key: "updated_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Update status date",
        sorter: true,
        icon: alertsColumnIconMap["updated_timestamp"],
        stateData: state,
        key: "updated_timestamp"
      }),
    dataIndex: "updated_timestamp",
    width: 180,
    render: dateCell
  },
  business_account: {
    key: "business_account",
    title: (state) =>
      titleSortFilters({
        title: "Business account",
        icon: alertsColumnIconMap["business_account"],
        stateData: state,
        key: "business_account"
      }),
    dataIndex: "business_account",
    width: 200,
    render: (value, record) =>
      value ? (
        <Flex gap={6} align="center">
          <Text
            className="blue"
            onClick={(e) => {
              e.stopPropagation()
              onBusinessAccount?.(record)
            }}
          >
            {value}
          </Text>
          <Text copyable={{ text: value, tooltips: false }} />
        </Flex>
      ) : (
        <Text>-</Text>
      )
  },
  user_full_name: {
    key: "user_full_name",
    title: (state) =>
      titleSortFilters({
        title: "User full name",
        icon: alertsColumnIconMap["user_full_name"],
        stateData: state,
        key: "user_full_name"
      }),
    dataIndex: "user_full_name",
    width: 185,
    render: (value) => <Text>{value || "-"}</Text>
  },
  user_email: {
    key: "user_email",
    title: (state) =>
      titleSortFilters({
        title: "User email",
        icon: alertsColumnIconMap["user_email"],
        stateData: state,
        key: "user_email"
      }),
    dataIndex: "user_email",
    width: 220,
    render: (value) => <Text>{value || "-"}</Text>
  }
})

export const getDefaultColumn = ({ field_id, label }) => ({
  key: field_id,
  title: (state) =>
    titleSortFilters({
      title: label,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  width: 200,
  render: (value) => <Text>{value || "-"}</Text>
})

export const getActionColumn = ({ onView }) => ({
  key: "actions",
  title: () => null,
  width: 72,
  fixed: "right",
  align: "center",
  render: (_, record) => (
    <Flex justify="center">
      <Tooltip title="View details">
        <ButtonComponent
          className="flex center dr-eye-btn"
          icon={<Icon type="eye" />}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onView(record)
          }}
        />
      </Tooltip>
    </Flex>
  )
})
